import { useContext, useRef, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Dropdown } from 'primereact/dropdown';
import { BreadCrumb } from 'primereact/breadcrumb';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { AuthContext } from '../../common/contexts/AuthContext';
import useAuthorization from '../../common/hooks/useAuthorization';
import './TopBar.css';

import {
  faCircleUser,
  faCaretDown,
  faGear,
  faGears,
  faScrewdriverWrench,
  faHouse,
  faRectangleList,
  faUsersCog,
  faUsers,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faLayerGroup,
  faListCheck,
  faBuildingFlag,
} from '@fortawesome/free-solid-svg-icons';

import { library } from '@fortawesome/fontawesome-svg-core';
import Logo from '../../assets/images/atlas-controls-logo.png';
import { Menubar } from 'primereact/menubar';

library.add(
  faGear,
  faGears,
  faScrewdriverWrench,
  faUsersCog,
  faHouse,
  faRectangleList,
  faUsers,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faLayerGroup,
  faListCheck,
  faBuildingFlag
);

const itemCreator = (pathname = '', nav, locationState) => {
  const pathnameCleaned = pathname.replace(/(\/\d+|\/true|\/false|\/null)/g, '');
  const paths = pathnameCleaned
    .split('/')
    .filter((path) => path !== '');

  const pathConfig = {
    mytaskpanel: {
      label: 'My Task Panel',
      command: () => nav('/mytaskpanel'),
    },
    mychecklists: {
      label: 'My Checklists',
      command: () => nav('/mychecklists'),
    },
    myChecklistPanel: {
      label: 'My Checklist Panel',
      command: () => nav(`/mychecklists/myChecklistPanel/${locationState.checkListId}`, {
        state: locationState,
      }),
    },
    buildchecklists: {
      label: 'Build Checklists',
      command: () => nav('/buildchecklists'),
    },
    buildchecklist: {
      label: 'Build Checklist',
      command: () => nav(`/buildchecklists/buildchecklist/${locationState.checklistId}`, {
        state: locationState,
      }),
    },
    searchtaskdefinitions: {
      label: 'Search Task Definitions',
    },
    buildtaskdefinitionpanel: {
      label: 'Build Task Definitions',
      command: () => nav('/buildtaskdefinitionpanel'),
    },
    buildtaskdefinition: {
      label: 'Build Task Definition',
    },
    buildtasktags: {
      label: 'Build Task Tags',
    },
    buildtaskdefinitions: {
      label: 'Build Task Definitions',
      command: () => nav('/buildtaskdefinitionpanel'),
    },
    buildtasklinks: {
      label: 'Build Task Links',
      command: () => nav(
        `/buildtaskdefinitions/buildtasklinks/${locationState.taskDefinitionId}`,
        { state: { ...locationState } }
      ),
    },
    buildtasklink: {
      label: 'Build Task Link',
    },
    buildtaskdocuments: {
      label: 'Build Task Documents',
      command: () => nav(
        `/buildtaskdefinitions/buildtaskdocuments/${locationState.taskDefinitionId}`,
        { state: { ...locationState } }
      ),
    },
    buildgroupdefinition: {
      label: 'Build Group Definition',
    },
    buildtaskdocument: {
      label: 'Build Task Document',
    },
    mychecklistusertasks: {
      label: 'My Checklist User Tasks',
    },
    mychecklistbulkcomments: {
      label: 'My Checklist Bulk Comments',
    },
    taskDashboard: {
      label: 'My Checklist Completed Tasks',
    },
    users: {
      label: 'Users',
      command: () => nav('/users'),
    },
    custommissions: {
      label: 'Custom Missions',
      command: () => nav('/custommissions'),
    },
    custommission: {
      label: 'Custom Mission',
    },
  };

  const allPaths = paths.map((path) => {
    if (pathConfig[path]) {
      return pathConfig[path];
    }

    const capFirstLetter = path.charAt(0).toUpperCase() + path.slice(1);
    return {
      label: capFirstLetter,
    };
  });
  allPaths.unshift({
    label: 'Dashboard',
    command: () => {
      nav('/');
    },
  });
  if (locationState && Object.values(locationState).length > 0) {
    Object.values(locationState).forEach((state) => {
      if (!/^\d+$/.test(state)) {
        if(!state instanceof Object) {
          allPaths.push({
            label: state,
          });
        }
      }
    });
  }
  return allPaths;
};

const TopBar = () => {
  const { sessionStore, sessionHelpers } = useContext(AuthContext);
  const { getMenuItemsForUser } = useAuthorization();
  const [loading, setLoading] = useState(true);
  const op = useRef(null);
  let isAuthenticated = sessionHelpers?.isAuthenticated();
  const { pathname, state } = useLocation();
  const [parsedMenu, setParsedMenu] = useState([]);

  const nav = useNavigate();

  const currentOrganizationId = sessionStore?.currentOrganizationId;

  const logoutHandler = (e) => {
    e.preventDefault();
    sessionHelpers.logoutSession();
  };

  const switchMissionHandler = (mission) => {
    sessionHelpers.switchMission(mission.code);
    setLoading(true);
  };

  const missions = sessionStore?.userMissions?.map((o) => {
    return {
      name: o.name,
      code: o.id.toString(),
    };
  });

  const currentMission =
    missions &&
    missions.find((mission) => Number(mission.code) === currentOrganizationId);


  useEffect(() => {
    if (loading && sessionHelpers?.isAuthenticated()) {
      const menuItems = getMenuItemsForUser();
      console.log('menuItem:', menuItems);

      setParsedMenu(
        menuItems.map((item) => {
          const id = item.id;
          const label = item.label;
          const url = item.target;
          const icon = item.icon;
          const items = [];
          if (item.subItems) {
            item.subItems.forEach((subitem) => {
              const item = {
                id: subitem.id,
                label: subitem.label,
                icon: subitem.icon ? (
                  <FontAwesomeIcon
                    icon={subitem.icon[1]}
                    className="mr-2"
                  ></FontAwesomeIcon>
                ) : (
                  subitem.icon
                ),
                command: () => {
                  if (subitem.target) {
                    nav(subitem.target);
                  }
                },
              };
              items.push(item);
            });
            return {
              id: id,
              label: label,
              icon: (
                <FontAwesomeIcon
                  icon={icon[1]}
                  className="mr-2"
                ></FontAwesomeIcon>
              ),
              command: () => {
                if (url) {
                  nav(url);
                }
              },
              items: items,
            };
          } else {
            return {
              id: id,
              label: label,
              icon: (
                <FontAwesomeIcon
                  icon={icon[1]}
                  className="mr-2"
                ></FontAwesomeIcon>
              ),
              command: () => {
                if (url) {
                  nav(url);
                }
              },
            };
          }
        })
      );
      setLoading(false);
    }
  }, [getMenuItemsForUser, loading, nav, sessionHelpers]);


  const start = (
    <>
      <Link className="navbar-brand text-white flex bg-adg-darkblue p-4" to="/">
        <img className="h-16" src={Logo} alt="Atlas Controls" />
      </Link>
    </>
  );

  const end = isAuthenticated && (
    <>
      <div className="ml-auto content-center p-4">
        {(() => {
          return (
            <>
              <Button
                text
                onClick={(e) => op.current.toggle(e)}
                className="text-white"
              >
                <FontAwesomeIcon icon={faCircleUser} className="me-1" />
                {sessionStore?.user?.name}
                {sessionStore && sessionStore.user === true && (
                  <Badge
                    severity="success"
                    className="ml-2"
                    value="Open Net"
                    pt={{ root: 'px-2' }}
                  />
                )}
                <FontAwesomeIcon icon={faCaretDown} className="ml-2 mr-2" />
              </Button>
              <OverlayPanel ref={op}>
                <Button text label="Sign Out" onClick={logoutHandler} />
              </OverlayPanel>
            </>
          );
        })()}
      </div>
    </>
  );

  const subStart = (
    <Menubar
      className="top-0 bg-white z-40 p-0 m-0 border-0 rounded-none text-sm w-full h-[60px]"
      start={
        <div className="p-2">
          <div className="flex flex-row content-center">
            <div className="mr-3">
              <Dropdown
                required
                id="currentMission"
                value={currentMission}
                name="Mission"
                options={missions}
                placeholder="Mission"
                optionLabel="name"
                onChange={(e) => {
                  switchMissionHandler(e.value);
                }}
              />
            </div>
            <BreadCrumb
              model={itemCreator(pathname, nav, state)}
              className="h-[2.875rem]"
            />
          </div>
        </div>
      }
    ></Menubar>
  );

  return isAuthenticated ? (
    <>
      <div className="shadow-sm sticky top-0 z-50 p-0 m-0 border-0 rounded-none text-sm">
        <Menubar
          className="top-0 bg-adg-gray-dark z-50 p-0 m-0 border-0 rounded-none text-sm"
          model={parsedMenu}
          start={start}
          end={end}
        ></Menubar>
        {subStart}
      </div>
    </>
  ) : (
    <Menubar
      className="shadow-sm sticky top-0 bg-adg-gray-dark z-50 p-0 m-0 border-0 rounded-none text-sm"
      start={start}
    ></Menubar>
  );
};

export default TopBar;
